import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Frontend-only marker for workspaces created through the self-serve /welcome
 * flow. Self-serve accounts stop at the connect step — the dashboard stays
 * gated until the SCC team activates the workspace.
 *
 * Known limitation (accepted for the frontend-guard phase): the marker lives
 * in localStorage, so it does not follow the user across browsers, and SCC
 * "activation" cannot clear it remotely. The durable version needs the
 * backend to expose the agency's `metadata.selfServe` flag on the workspace
 * DTO and enforce the gate server-side.
 */
interface SelfServeStore {
  orgIds: Record<string, true>
  mark: (orgId: string) => void
}

export const useSelfServeStore = create<SelfServeStore>()(
  persist(
    (set) => ({
      orgIds: {},
      mark: (orgId) => set((s) => ({ orgIds: { ...s.orgIds, [orgId]: true } })),
    }),
    { name: 'scc-self-serve-orgs' },
  ),
)

/** True when this org's dashboard should be gated for the current path —
 * self-serve orgs may only visit their onboarding (connect) pages. */
export function shouldGateSelfServe(orgId: string, pathname: string): boolean {
  return !!useSelfServeStore.getState().orgIds[orgId] && !pathname.includes('/onboarding/')
}
